import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import { AdminPickCell, AdminPlainBar, AdminRowDelete, useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

const EMPTY = { code: '', name: '', organisationType: 'company', address: '', contactName: '', contactEmail: '', isActive: true };

const PartnersPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  useDocumentTitle('Partners');
  const load = () => platformAPI.listPartners().then((response) => setRows(response.data?.data?.partners || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const admin = useAdminRecordDelete({
    entity: 'partner',
    noun: 'partner',
    plural: 'partners',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'partner' });
  const save = async (event) => {
    event.preventDefault();
    try {
      await platformAPI.savePartner(form);
      setForm(EMPTY);
      setMessage({ variant: 'success', text: 'Partner saved. Inactive partners stay on old tenders and cannot be newly selected.' });
      await load();
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save the partner' });
    }
  };
  if (loading) return <Spinner animation="border" size="sm" />;
  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}><BiArrowBack /> Masters</button>
      <h1 className="h4">Partners</h1>
      <p className="text-muted">Reusable JV and consortium partners. Tender-specific due diligence is recorded on the tender, not on this master.</p>
      {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
      {admin.notice}
      <form className="row g-2 mb-3" onSubmit={save}>
        <div className="col-md-2"><Form.Control placeholder="Code" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} required /></div>
        <div className="col-md-3"><Form.Control placeholder="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></div>
        <div className="col-md-3"><Form.Control placeholder="Address" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} /></div>
        <div className="col-md-2"><Form.Control placeholder="Contact" value={form.contactName} onChange={(event) => setForm({ ...form, contactName: event.target.value })} /></div>
        <div className="col-md-2"><Button size="sm" type="submit">Save</Button></div>
      </form>
      <AdminPlainBar admin={admin} rows={rows} />
      <Table size="sm">
        <thead><tr>{admin.enabled ? <th /> : null}<th>Code</th><th>Name</th><th>Rev.</th><th>Status</th><th /></tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row._id}>
            <AdminPickCell admin={admin} id={row._id} />
            <td>{row.code}</td>
            <td>{row.name}</td>
            <td>{row.revision || 1}</td>
            <td>
              <span className={`cust-status cust-status--${row.isActive === false ? 'inactive' : 'active'}`}>
                {row.isActive === false ? 'Inactive' : 'Active'}
              </span>
            </td>
            <td>
              <Button size="sm" variant="link" onClick={() => revision.open(row)}>Revision info</Button>
              <AdminRowDelete admin={admin} id={row._id} label={row.name} />
            </td>
          </tr>
        ))}</tbody>
      </Table>
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default PartnersPage;
